// Centralized PT-BR strings for the public-facing UI.
// When the time comes to i18n, this file becomes the seed of the messages catalog.

export const messages = {
  // Loading / errors (short labels · used inline)
  errorGeneric: 'Algo deu errado. Tenta de novo?',
  errorNotFound: 'Página não encontrada',
  errorBrokerNotFound: 'Corretor não encontrado',
  errorSubdomainMissing:
    'Subdomínio não detectado. Acesse via subdomínio do corretor (ex: angel.localhost:5173) ou use ?broker=angel.',

  // Full-screen empty-state copy
  emptyBrokerNotFoundTitle: 'Corretor não encontrado',
  emptyBrokerNotFoundBody:
    'Não encontramos um corretor com esse endereço no NexoFácil. Verifique o link ou peça pra ele te enviar de novo.',
  emptyPropertyNotFoundTitle: 'Empreendimento não encontrado',
  emptyPropertyNotFoundBody:
    'Este empreendimento foi removido ou nunca existiu. Dê uma olhada nos outros que o corretor oferece.',
  emptyGenericTitle: 'Algo deu errado por aqui',
  emptyGenericBody:
    'Atualize a página em alguns instantes. Se o problema continuar, fale com o seu corretor.',
  empty404Title: 'Página não encontrada',
  empty404Body: 'O link que você seguiu está quebrado ou a página foi movida.',
  ctaTryAgain: 'Atualizar página',
  ctaBackHome: 'Voltar pra página inicial',
  ctaSeeProperties: 'Ver outros empreendimentos',

  // Property detail
  propertyAboutTitle: 'Sobre o empreendimento',
  propertyUnitTypesTitle: 'Tipologias disponíveis',
  propertyFeaturesTitle: 'Diferenciais',
  propertyPriceFromLabel: 'A partir de',
  propertyBedroomsShort: 'dorm',
  propertyBathroomsShort: 'banh',
  propertyParkingShort: 'vaga',
  propertyAreaShort: 'm²',

  fieldMonthlyIncome: 'Renda mensal total da família',
  fieldIncomeSource: 'Como você comprova renda?',
  fieldHasIncomeTaxDeclaration: 'Você já declarou imposto de renda?',
  fieldHasIncomeTaxDeclaration_hint:
    'Pra autônomos e MEI, a Caixa exige comprovação de renda via IR (Imposto de Renda) ou DECORE.',
  fieldAge: 'Idade',
  fieldMaritalStatus: 'Estado civil',
  fieldDependents: 'Quantos dependentes?',
  fieldHasFGTS: 'Você tem saldo de FGTS?',
  fieldFGTSBalance: 'Saldo aproximado de FGTS',
  fieldOwnsProperty: 'Possui imóvel próprio onde mora ou trabalha?',
  fieldHasUsedMCMV: 'Você já comprou algum imóvel pelo MCMV?',
  fieldCreditStatus: 'Como está seu nome no SPC/Serasa?',
  fieldDownPayment: 'Você tem dinheiro guardado pra entrada?',
  fieldDownPaymentAmount: 'Quanto você tem disponível?',
  fieldTimeline: 'Quando pretende fechar?',

  incomeSource_clt: 'CLT (carteira assinada)',
  incomeSource_self_employed: 'Autônomo/MEI',
  incomeSource_business_owner: 'Empresário',
  incomeSource_retired: 'Aposentado / pensionista',
  incomeSource_informal: 'Renda informal',
  incomeSource_informal_hint: 'Comprovação por extrato bancário + declaração de IR',
  incomeSource_other: 'Outro',

  maritalStatus_single: 'Solteiro(a)',
  maritalStatus_married: 'Casado(a)',
  maritalStatus_divorced: 'Divorciado(a)',
  maritalStatus_widowed: 'Viúvo(a)',
  maritalStatus_stable_union: 'União estável',

  creditStatus_clean: 'Limpo',
  creditStatus_restricted: 'Com restrição',
  creditStatus_unknown: 'Não sei',

  timeline_within_1_month: 'Até 1 mês',
  timeline_1_to_3_months: '1 a 3 meses',
  timeline_3_to_6_months: '3 a 6 meses',
  timeline_6_plus_months: 'Mais de 6 meses',

  // Result · copy unificado (sem expor status binário pro lead). O envio no
  // WhatsApp é o passo final, não um extra: copy que diz "o corretor vai
  // analisar" deixa o lead esperando contato e ele nunca chega na conversa.
  resultEyebrow: 'Falta só 1 passo',
  resultTitle: 'Agora é só enviar sua mensagem',
  // Sem citar o nome do corretor: "É por lá que +Lar te mostra" soava
  // estranho quando o tenant é empresa. O nome já está no botão.
  resultBody:
    'Toque no botão abaixo: o WhatsApp abre com a mensagem pronta, é só apertar enviar. É por lá que você recebe as condições e o próximo passo.',
  resultPvBody:
    'Toque no botão abaixo: o WhatsApp abre com os dados do seu veículo já na mensagem, é só apertar enviar. É por lá que você recebe a cotação.',
  ctaSendWhatsapp: (name?: string) =>
    name ? `Enviar mensagem pra ${name}` : 'Enviar mensagem no WhatsApp',
  resultCtaHint: 'Sem custo e sem compromisso.',
  resultMcmvNextStepsTitle: 'Enquanto isso, adianta:',
  resultMcmvNextSteps: [
    'Separa RG, CPF e comprovante de renda dos últimos 3 meses',
    'Se tiver FGTS, tenha o extrato à mão (fácil no app da Caixa)',
    'Pensa em quantos meses você pretende dar entrada',
  ],
  // Imobiliário geral (compra fora da régua MCMV, ex: Angélica Coelho)
  resultIgNextSteps: [
    'Separe RG, CPF e comprovante de renda dos últimos 3 meses',
    'Se for usar o carro na entrada, tenha o documento dele (CRLV) à mão',
    'Liste os bairros e as cidades que fazem sentido pra você',
  ],
  resultIgDisclaimer:
    'Esta é uma análise inicial · o atendimento te acompanha da escolha do imóvel até a assinatura.',
  resultDisclaimer:
    'Esta é uma análise inicial · o corretor te acompanha nas próximas etapas até a aprovação final do banco.',

  // Result · PV (Risco Zero)
  resultPvNextStepsTitle: 'Enquanto o consultor responde:',
  resultPvNextSteps: [
    'Tenha o documento do veículo (CRLV) à mão',
    'Anote a placa e o ano se ainda não passou',
    'Sem análise de crédito · adesão é rápida',
  ],
  resultPvDisclaimer:
    'Proteção veicular tem regras e regulamento próprios · diferente de seguro tradicional. O consultor explica as condições antes da adesão.',

  // Failed criteria · usados só no painel admin/broker (não mais no lead final).
  criterion_income_above_max: 'Renda acima do teto do MCMV',
  criterion_income_below_min: 'Renda abaixo do mínimo do nosso portfólio',
  criterion_under_min_age: 'Idade abaixo do mínimo (18 anos)',
  criterion_already_owns_property: 'Já possui um imóvel próprio',
  criterion_already_used_mcmv: 'Já usou o MCMV anteriormente',
  criterion_no_fgts: 'Sem saldo de FGTS',
  criterion_restricted_credit: 'Restrição no SPC/Serasa',
  criterion_no_down_payment: 'Sem reserva pra entrada',
  criterion_no_income_tax_declaration: 'Sem declaração de IR (autônomo/MEI)',
} as const;
